<template>
    <div
        :href="link"
        :class="classObject"
        :style="styleBackground"
        @click="$emit('click')"
        :is="link ? 'a' : 'div'"
        :target="buildTarget(link)"
    >
        <div
            class="z-card__helper"
            v-if="image || padding"
            :style="styleHelperPadding"
        >
            <div :style="styleHelperImage" class="z-card__helper-image"></div>
            <div :style="styleOverlay" class="z-card__overlay">
                <slot name="overlay-content" />
            </div>
        </div>
        <div class="z-card__body">
            <div
                :style="styleHelperImage"
                v-if="theme === 'media' || theme === 'bods'"
                :class="[
                    'z-card__image',
                    { 'z-card__image--preview' : !image }
                ]"
            >
                <z-icon
                    width="50px"
                    height="50px"
                    v-if="!image"
                    name="press-release"
                ></z-icon>
                <img
                    :src="icon"
                    class="z-card__icon"
                    v-if="icon"
                />
            </div>
            <div class="z-card__wrapper">
                <div
                    :class="[
                        'z-card__top',
                        { 'is-animated': isAnimated }
                    ]"
                    ref="card-top"
                    v-if="this.$slots['top'] || this.$slots['top-left'] || this.$slots['top-right']"
                >
                    <div
                        class="z-card__top-up"
                        v-if="this.$slots['top']"
                    >
                        <slot name="top" />
                    </div>
                    <div
                        class="z-card__top-left"
                        v-if="this.$slots['top-left']"
                    >
                        <slot name="top-left" />
                    </div>
                    <div
                        class="z-card__top-right"
                        v-if="this.$slots['top-right']"
                    >
                        <slot name="top-right" />
                    </div>
                </div>
                <span
                    class="z-card__title"
                    v-if="this.$slots['title']"
                >
                    <slot name="title" />
                </span>

                <div
                    v-if="this.$slots['content']"
                    :class="[
                        'z-card__content',
                        {
                            'z-card__content--without-bottom': !this.$slots['bottom'] && !this.$slots['bottom-static'],
                            'u-collapse--all': wideContent
                        }
                    ]"
                >
                    <slot name="content"></slot>
                </div>
                <div
                    v-if="this.$slots['bottom-static'] || this.$slots['bottom'] || this.$slots['bottom-fixed'] || this.$slots['bottom-left'] || this.$slots['bottom-right']"
                    :class="['z-card__bottom', { 'z-card__bottom--inherit' : this.$slots['bottom-left'] || this.$slots['bottom-right'], 'z-card__bottom--fixed' : this.$slots['bottom-fixed']}]"
                >
                    <div v-if="this.$slots['bottom-static']" class="z-card__bottom-static">
                        <slot name="bottom-static"></slot>
                    </div>
                    <div v-if="this.$slots['bottom-fixed']" class="z-card__bottom-fixed">
                        <slot name="bottom-fixed"></slot>
                    </div>
                    <div v-if="this.$slots['bottom']" class="z-card__bottom-down">
                        <slot name="bottom"></slot>
                    </div>
                    <div
                        class="z-card__bottom-left"
                        v-if="this.$slots['bottom-left']"
                    >
                        <slot name="bottom-left" />
                    </div>
                    <div
                        class="z-card__bottom-right"
                        v-if="this.$slots['bottom-right']"
                    >
                        <slot name="bottom-right" />
                    </div>
                </div>
                <div
                    :class="[
                        'z-card__step',
                        { 'z-card__step--zero' : step === '0' },
                        { 'z-card__step--long' : step.length > 1 }
                    ]"
                    v-if="step"
                >
                    <span v-html="step === '0' ? '' : step"></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import verge from 'verge'
import { debounce } from 'throttle-debounce'

export default {
    name: 'z-card',
    props: {
        link: String,
        overlay: String,
        background: String,
        image: String,
        icon: String,
        padding: String,
        wideContent: {
            type: Boolean,
            default: false
        },
        theme: String,
        step: String,
        dividerColor: String,
        border: {
            type: Boolean,
            default: true
        },
        shadow: Boolean,
        fullWidth: Boolean,
        fullHeight: Boolean,
        autoHeight: Boolean,
        withIcon: Boolean
    },
    data () {
        return {
            isAnimated: false
        }
    },
    methods: {
        buildTarget (href) {
            return /^http|^\/\//.test(href) ? '_blank' : false
        },
        checkCard () {
            if (this.isAnimated) {
                window.removeEventListener('scroll', this.checkCard)
                return
            }
            if (verge.inViewport(this.$refs['card-top'], -300)) {
                this.isAnimated = true
            }
        }
    },
    mounted () {
        if (this.theme === 'colored') {
            window.addEventListener('scroll', debounce(10, e => {
                this.checkCard()
            }))
            this.checkCard()
        }
    },
    computed: {
        classObject () {
            let arrClass = [
                'z-card',
                {
                    'z-card--link': this.link,
                    'z-card--shadow': this.shadow,
                    'z-card--padding': this.padding,
                    'z-card--full-width': this.fullWidth,
                    'z-card--full-height': this.fullHeight,
                    'z-card--auto-height': this.autoHeight,
                    'z-card--with-icon': this.withIcon,
                    'z-card--border': this.border
                }
            ]

            if (this.theme) {
                arrClass.push(`z-card--theme-${this.theme}`)

                if (!this.step && this.theme === 'steps') {
                    arrClass.push(`z-card--theme-${this.theme}-no`)
                }
            }

            if (this.dividerColor) {
                arrClass.push(`z-card--divider-${this.dividerColor}`)
            }

            return arrClass
        },
        styleHelperPadding () {
            let style = ''

            if (this.padding) {
                style += `padding-top: ${this.padding}`
            }

            return style
        },
        styleHelperImage () {
            let style = ''

            if (this.image) {
                style = `background-image: url('${this.image}');`
            }

            return style
        },
        styleOverlay () {
            let style = ''

            if (this.overlay) {
                style = `background: ${this.overlay};`
            }

            return style
        },
        styleBackground () {
            let style = ''

            if (this.background) {
                style = `background-color: ${this.background};`
            }

            return style
        }
    }
}
</script>

<style lang="scss" src="./index.scss"></style>
